import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone, PhoneOff, PhoneIncoming, Mic, MicOff,
  X, ChevronDown, Settings, User, Clock, Volume2,
  PhoneForwarded, Search, ArrowRight, Pause, Play, Check, XCircle,
} from 'lucide-react';
import { usePhone, PhoneConfig } from '../../hooks/usePhone';
import { apiClient } from '@/lib/api-client';

// ─── Types ────────────────────────────────────────────────────────────────────

type WidgetView = 'minimised' | 'dialpad' | 'calls' | 'settings' | 'incoming' | 'in_call' | 'save_contact';

// Default server configuration - can be overridden in settings
const DEFAULT_CONFIG = {
  wsServer: 'wss://support.mrvhub.com/ast-ws',
  sipDomain: '192.168.50.14',
  stunServer: '192.168.50.14:3478',
  turnServer: '192.168.50.14:3478',
  vpnMode: true, // Default to VPN mode for internal users
};

interface PRDContact {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  company_name?: string;
  job_title?: string;
  contact_type?: string;
}

interface SoftphoneSettings {
  extension: string;
  password: string;
  displayName: string;
  wsServer: string;
  sipDomain: string;
  vpnMode: boolean;
  stunServer: string;
  turnServer: string;
  turnUser: string;
  turnPass: string;
  ringtone?: string; // base64 data URL of custom ringtone file
}

interface ExtensionInfo {
  extension: string;
  name: string;
  department?: string;
  status: 'available' | 'busy' | 'offline' | 'unknown';
}

type TransferMode = 'blind' | 'attended' | null;

// ─── Ring Tones (Web Audio API) ──────────────────────────────────────────────

function createRingTone(ctx: AudioContext, pattern: 'incoming' | 'outgoing'): () => void {
  let stopped = false;
  let timeout: ReturnType<typeof setTimeout>;

  function playTone(freq1: number, freq2: number, dur: number, when: number, volume: number = 0.5) {
    // Create dual-tone for more realistic ring
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.frequency.value = freq1;
    osc2.frequency.value = freq2;
    osc1.type = 'sine';
    osc2.type = 'sine';

    // Envelope: quick attack, sustain, quick release
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(volume, when + 0.02);
    gain.gain.setValueAtTime(volume, when + dur - 0.02);
    gain.gain.linearRampToValueAtTime(0, when + dur);

    osc1.start(when);
    osc2.start(when);
    osc1.stop(when + dur);
    osc2.stop(when + dur);
  }

  function ringLoop() {
    if (stopped) return;
    const now = ctx.currentTime;
    if (pattern === 'incoming') {
      // UK-style double ring: ring-ring ... pause (louder, more urgent)
      playTone(400, 450, 0.4, now, 0.6);
      playTone(400, 450, 0.4, now + 0.5, 0.6);
      timeout = setTimeout(ringLoop, 2000); // Faster repeat for incoming
    } else {
      // Ringback tone (single tone, quieter)
      playTone(400, 450, 1.0, now, 0.3);
      timeout = setTimeout(ringLoop, 3000);
    }
  }
  ringLoop();
  return () => { stopped = true; clearTimeout(timeout); };
}

// ─── Call State Persistence ──────────────────────────────────────────────────

const CALL_STATE_KEY = 'softphone_call_state';

interface PersistedCallState {
  inCall: boolean;
  number: string;
  startTime: number;
  direction: 'inbound' | 'outbound';
}

function saveCallState(state: PersistedCallState | null) {
  if (state) {
    sessionStorage.setItem(CALL_STATE_KEY, JSON.stringify(state));
  } else {
    sessionStorage.removeItem(CALL_STATE_KEY);
  }
}

function loadCallState(): PersistedCallState | null {
  const saved = sessionStorage.getItem(CALL_STATE_KEY);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  }
  return null;
}

function fullName(c: PRDContact) {
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown';
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function loadSettings(): SoftphoneSettings {
  const saved = localStorage.getItem('softphone_settings');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      // Fall through to defaults
    }
  }
  return {
    extension: localStorage.getItem('sip_ext') || '',
    password: localStorage.getItem('sip_pass') || '',
    displayName: '',
    wsServer: DEFAULT_CONFIG.wsServer,
    sipDomain: DEFAULT_CONFIG.sipDomain,
    vpnMode: DEFAULT_CONFIG.vpnMode,
    stunServer: DEFAULT_CONFIG.stunServer,
    turnServer: DEFAULT_CONFIG.turnServer,
    turnUser: '',
    turnPass: '',
    ringtone: '',
  };
}

function saveSettings(settings: SoftphoneSettings) {
  localStorage.setItem('softphone_settings', JSON.stringify(settings));
  // Also save legacy keys for backwards compatibility
  localStorage.setItem('sip_ext', settings.extension);
  localStorage.setItem('sip_pass', settings.password);
}

function buildConfig(settings: SoftphoneSettings): PhoneConfig | null {
  if (!settings.extension || !settings.password) return null;
  return {
    sipUri: `sip:${settings.extension}@${settings.sipDomain}`,
    sipPassword: settings.password,
    wsServer: settings.wsServer,
    sipDomain: settings.sipDomain,
    displayName: settings.displayName || `Ext ${settings.extension}`,
    vpnMode: settings.vpnMode,
    stunServer: settings.stunServer,
    turnServer: settings.turnServer,
    turnUser: settings.turnUser,
    turnPass: settings.turnPass,
  };
}

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: string }) {
  const colour =
    status === 'registered' ? 'bg-green-500' :
    status === 'registering' ? 'bg-amber-400 animate-pulse' :
    status === 'in_call' ? 'bg-blue-500 animate-pulse' :
    status === 'disconnected' ? 'bg-red-500 animate-pulse' :
    'bg-gray-400';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colour}`} />;
}

// ─── Main Widget ──────────────────────────────────────────────────────────────

export function SoftphoneWidget() {
  const [view, setView] = useState<WidgetView>('minimised');
  const [settings, setSettings] = useState<SoftphoneSettings>(loadSettings);
  const [settingsDraft, setSettingsDraft] = useState<SoftphoneSettings>(settings);
  const [config, setConfig] = useState<PhoneConfig | null>(() => buildConfig(settings));
  const [dialInput, setDialInput] = useState('');
  const [incomingContact, setIncomingContact] = useState<PRDContact | null>(null);
  const [incomingNumber, setIncomingNumber] = useState('');
  const [lookupDone, setLookupDone] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [saveForm, setSaveForm] = useState({ first_name: '', last_name: '', company_name: '' });
  const [saving, setSaving] = useState(false);
  const [recentCalls, setRecentCalls] = useState<Array<{ number: string; direction: string; duration: number; ts: number }>>([]);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Transfer state
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferMode, setTransferMode] = useState<TransferMode>(null);
  const [transferTarget, setTransferTarget] = useState('');
  const [extensions, setExtensions] = useState<ExtensionInfo[]>([]);
  const [extensionsLoading, setExtensionsLoading] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const stopRingRef = useRef<(() => void) | null>(null);
  const callStartRef = useRef<number>(0);

  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

  // Create audio element on first render (before any calls can happen)
  if (remoteAudioRef.current === null && typeof document !== 'undefined') {
    const existingAudio = document.getElementById('softphone-remote-audio') as HTMLAudioElement;
    if (existingAudio) {
      remoteAudioRef.current = existingAudio;
    } else {
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.id = 'softphone-remote-audio';
      document.body.appendChild(audio);
      remoteAudioRef.current = audio;
      console.log('[SoftphoneWidget] Created remote audio element');
    }
  }

  // ── Ring helpers ─────────────────────────────────────────────────────────

  const startRing = useCallback((pattern: 'incoming' | 'outgoing') => {
    if (stopRingRef.current) { stopRingRef.current(); stopRingRef.current = null; }
    // Use custom ringtone MP3 for incoming calls if configured
    if (pattern === 'incoming' && settings.ringtone) {
      const audio = new Audio(settings.ringtone);
      audio.loop = true;
      audio.volume = 0.8;
      audio.play().catch(() => {});
      stopRingRef.current = () => { audio.pause(); audio.currentTime = 0; };
      return;
    }
    // Fallback: synthetic Web Audio ring
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();
    stopRingRef.current = createRingTone(ctx, pattern);
  }, [settings.ringtone]);

  const stopRing = useCallback(() => {
    if (stopRingRef.current) { stopRingRef.current(); stopRingRef.current = null; }
  }, []);

  // ── Lookup ────────────────────────────────────────────────────────────────

  const lookupPhone = useCallback(async (number: string) => {
    setLookupDone(false);
    try {
      const res = await apiClient.get('/contacts/lookup', { params: { phone: number } });
      if (res.data?.id) setIncomingContact(res.data);
    } catch { /* unknown */ }
    setLookupDone(true);
  }, []);

  // ── Log call (interaction) ─────────────────────────────────────────────────

  const logCall = useCallback(async (contactId: string | null, direction: string, duration: number, number: string) => {
    recentCalls.unshift({ number, direction, duration, ts: Date.now() });
    setRecentCalls([...recentCalls.slice(0, 20)]);
    if (!contactId) return;
    try {
      await apiClient.post(`/contacts/${contactId}/interactions`, {
        interaction_type: 'call',
        source: direction,
        subject: `${direction === 'inbound' ? 'Inbound' : 'Outbound'} call`,
        content: `Duration: ${formatDuration(duration)}`,
        metadata: { duration, number },
      });
    } catch { /* non-fatal */ }
  }, [recentCalls]);

  // ── Phone hook callbacks ───────────────────────────────────────────────────

  const handleIncoming = useCallback((session: any, callerNumber: string) => {
    setIncomingNumber(callerNumber);
    setIncomingContact(null);
    setView('incoming');
    lookupPhone(callerNumber);
    startRing('incoming');
  }, [lookupPhone, startRing]);

  const {
    status, incomingCall, currentSession, answer, hangup, call, mute, unmute, isMuted, sendDTMF,
    hold, unhold, isOnHold, blindTransfer, attendedTransfer, consultSession, completeAttendedTransfer, cancelAttendedTransfer
  } = usePhone({
    config,
    onIncomingCall: handleIncoming,
    remoteAudioRef,
  });

  // ── Call timer ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (status === 'in_call') {
      callStartRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
      }, 1000);

      // Save call state for persistence
      saveCallState({
        inCall: true,
        number: incomingNumber || dialInput,
        startTime: callStartRef.current,
        direction: incomingNumber ? 'inbound' : 'outbound',
      });
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setCallDuration(0);

      // Clear call state when call ends
      saveCallState(null);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status, incomingNumber, dialInput]);

  // ── Warn before page refresh during active call ─────────────────────────

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (status === 'in_call' || incomingCall) {
        e.preventDefault();
        e.returnValue = 'You have an active call. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status, incomingCall]);

  // ── Check for interrupted call on mount ─────────────────────────────────

  const [wasDisconnected, setWasDisconnected] = useState<PersistedCallState | null>(() => {
    const savedState = loadCallState();
    if (savedState?.inCall) {
      // Clear it immediately - we can't reconnect
      saveCallState(null);
      return savedState;
    }
    return null;
  });

  // ── Auto-transition to in_call view ──────────────────────────────────────

  useEffect(() => {
    if (status === 'in_call') { stopRing(); if (view !== 'in_call') setView('in_call'); }
  }, [status, view, stopRing]);

  // ── After call ends: show save prompt for unknown ─────────────────────────

  const prevStatus = useRef(status);
  useEffect(() => {
    if (prevStatus.current === 'in_call' && status !== 'in_call') {
      const dur = Math.floor((Date.now() - callStartRef.current) / 1000);
      logCall(incomingContact?.id || null, 'inbound', dur, incomingNumber);
      if (!incomingContact && incomingNumber) {
        setSaveForm({ first_name: '', last_name: '', company_name: '' });
        setView('save_contact');
      } else {
        setView('minimised');
      }
    }
    prevStatus.current = status;
  }, [status, incomingContact, incomingNumber, logCall]);

  // ── Save new contact ──────────────────────────────────────────────────────

  const handleSaveContact = async () => {
    setSaving(true);
    try {
      await apiClient.post('/contacts', {
        ...saveForm,
        phone: incomingNumber,
        contact_type: 'customer',
      });
    } catch { /* non-fatal */ }
    setSaving(false);
    // Return to in-call view if still connected, otherwise minimise
    setView(status === 'in_call' ? 'in_call' : 'minimised');
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAnswer = () => { stopRing(); answer(); setView('in_call'); };
  const handleDecline = () => {
    stopRing();
    if (incomingCall) incomingCall.session.terminate();
    logCall(null, 'inbound', 0, incomingNumber);
    setView('minimised');
  };
  const handleHangup = () => { hangup(); };
  const handleDial = () => {
    if (dialInput.trim()) {
      startRing('outgoing');
      call(dialInput.trim());
      setView('in_call');
    }
  };
  const handleMute = () => {
    if (isMuted) {
      unmute();
    } else {
      mute();
    }
  };

  // ── Transfer handlers ────────────────────────────────────────────────────

  const fetchExtensions = useCallback(async (search?: string) => {
    setExtensionsLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await apiClient.get('/softphone/extensions', { params });
      setExtensions(res.data || []);
    } catch (err) {
      console.error('[SoftphoneWidget] Failed to fetch extensions:', err);
      setExtensions([]);
    }
    setExtensionsLoading(false);
  }, []);

  const openTransferDialog = useCallback(() => {
    setShowTransferDialog(true);
    setTransferMode(null);
    setTransferTarget('');
    fetchExtensions();
  }, [fetchExtensions]);

  const closeTransferDialog = useCallback(() => {
    setShowTransferDialog(false);
    setTransferMode(null);
    setTransferTarget('');
  }, []);

  const handleBlindTransfer = useCallback((target: string) => {
    blindTransfer(target);
    closeTransferDialog();
  }, [blindTransfer, closeTransferDialog]);

  const handleAttendedTransfer = useCallback(async (target: string) => {
    setTransferMode('attended');
    await attendedTransfer(target);
    closeTransferDialog();
  }, [attendedTransfer, closeTransferDialog]);

  const handleCompleteTransfer = useCallback(() => {
    completeAttendedTransfer();
  }, [completeAttendedTransfer]);

  const handleCancelTransfer = useCallback(() => {
    cancelAttendedTransfer();
  }, [cancelAttendedTransfer]);

  const handleHold = useCallback(() => {
    if (isOnHold) {
      unhold();
    } else {
      hold();
    }
  }, [isOnHold, hold, unhold]);

  const handleSaveSettings = () => {
    saveSettings(settingsDraft);
    setSettings(settingsDraft);
    setConfig(buildConfig(settingsDraft));
    setView('minimised');
  };

  const handleDialpadKey = (digit: string) => {
    if (status === 'in_call') {
      // Send DTMF during call
      sendDTMF(digit);
    } else {
      // Add to dial input
      setDialInput(v => v + digit);
    }
  };

  // Test audio output
  const testAudio = () => {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.frequency.value = 440;
    gainNode.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      ctx.close();
    }, 500);
  };


  // ─── RENDER ───────────────────────────────────────────────────────────────

  // Incoming call — full overlay card
  if (view === 'incoming') {
    return (
      <div className="fixed bottom-6 right-6 z-[9999] w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-white text-center">
          <PhoneIncoming className="w-10 h-10 mx-auto mb-2 animate-bounce" />
          <p className="text-sm opacity-80 mb-1">Incoming call</p>
          <p className="text-xl font-bold">{incomingNumber || 'Unknown'}</p>
          {lookupDone && incomingContact ? (
            <div className="mt-2 text-sm opacity-90">
              <p className="font-semibold">{fullName(incomingContact)}</p>
              {incomingContact.company_name && <p className="opacity-80">{incomingContact.company_name}</p>}
            </div>
          ) : lookupDone ? (
            <p className="mt-2 text-sm opacity-70">Unknown caller</p>
          ) : (
            <p className="mt-2 text-sm opacity-70">Looking up...</p>
          )}
        </div>
        <div className="flex gap-3 p-4">
          <button onClick={handleDecline} className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium text-sm transition-colors">
            <PhoneOff className="w-4 h-4" /> Decline
          </button>
          <button onClick={handleAnswer} className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium text-sm transition-colors">
            <Phone className="w-4 h-4" /> Answer
          </button>
        </div>
      </div>
    );
  }

  // In call
  if (view === 'in_call') {
    // Check if we're in an attended transfer consultation
    const inConsultation = !!consultSession;

    return (
      <>
        {/* Transfer Dialog Overlay */}
        {showTransferDialog && (
          <div className="fixed bottom-6 right-6 z-[10000] w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <PhoneForwarded className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Transfer Call</span>
              </div>
              <button onClick={closeTransferDialog} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Transfer mode selection */}
            {!transferMode && (
              <div className="p-4 space-y-3">
                <p className="text-xs text-gray-500 mb-3">Choose transfer type:</p>
                <button
                  onClick={() => setTransferMode('blind')}
                  className="w-full p-3 border border-gray-200 rounded-xl hover:bg-gray-50 text-left transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Blind Transfer</p>
                      <p className="text-xs text-gray-500">Transfer immediately without announcing</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setTransferMode('attended')}
                  className="w-full p-3 border border-gray-200 rounded-xl hover:bg-gray-50 text-left transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Attended Transfer</p>
                      <p className="text-xs text-gray-500">Speak with target before transferring</p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Extension search and list */}
            {transferMode && (
              <div className="p-4 space-y-3">
                <button onClick={() => setTransferMode(null)} className="text-xs text-blue-600 hover:text-blue-700">
                  ← Back to transfer options
                </button>
                <p className="text-xs font-medium text-gray-700">
                  {transferMode === 'blind' ? 'Blind Transfer' : 'Attended Transfer'} to:
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={transferTarget}
                    onChange={(e) => {
                      setTransferTarget(e.target.value);
                      fetchExtensions(e.target.value || undefined);
                    }}
                    placeholder="Search extensions or enter number..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Extension list */}
                <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
                  {extensionsLoading ? (
                    <div className="py-4 text-center text-sm text-gray-400">Loading...</div>
                  ) : extensions.length === 0 ? (
                    <div className="py-4 text-center text-sm text-gray-400">No extensions found</div>
                  ) : (
                    extensions.map((ext) => (
                      <button
                        key={ext.extension}
                        onClick={() => {
                          if (transferMode === 'blind') {
                            handleBlindTransfer(ext.extension);
                          } else {
                            handleAttendedTransfer(ext.extension);
                          }
                        }}
                        disabled={ext.status === 'offline'}
                        className={`w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors ${ext.status === 'offline' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-800">{ext.name}</p>
                          <p className="text-xs text-gray-500">{ext.extension} · {ext.department}</p>
                        </div>
                        <span className={`w-2 h-2 rounded-full ${
                          ext.status === 'available' ? 'bg-green-500' :
                          ext.status === 'busy' ? 'bg-amber-500' :
                          'bg-gray-400'
                        }`} />
                      </button>
                    ))
                  )}
                </div>

                {/* Manual transfer button */}
                {transferTarget && !extensions.find(e => e.extension === transferTarget) && (
                  <button
                    onClick={() => {
                      if (transferMode === 'blind') {
                        handleBlindTransfer(transferTarget);
                      } else {
                        handleAttendedTransfer(transferTarget);
                      }
                    }}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <PhoneForwarded className="w-4 h-4" />
                    Transfer to {transferTarget}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Main In-Call Widget */}
        <div className="fixed bottom-6 right-6 z-[9999] w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className={`p-5 text-white text-center ${
            isOnHold ? 'bg-gradient-to-br from-amber-500 to-amber-600' :
            inConsultation ? 'bg-gradient-to-br from-purple-500 to-purple-700' :
            'bg-gradient-to-br from-blue-500 to-blue-700'
          }`}>
            <div className="flex items-center justify-center gap-2 mb-1">
              {isOnHold ? (
                <>
                  <Pause className="w-4 h-4" />
                  <span className="text-sm font-medium">On Hold</span>
                </>
              ) : inConsultation ? (
                <>
                  <Phone className="w-4 h-4 animate-pulse" />
                  <span className="text-sm font-medium">Consultation</span>
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4 animate-pulse" />
                  <span className="text-sm font-medium">In call</span>
                </>
              )}
            </div>
            <p className="text-lg font-bold">{incomingContact ? fullName(incomingContact) : (incomingNumber || dialInput)}</p>
            {incomingContact?.company_name && <p className="text-xs opacity-80">{incomingContact.company_name}</p>}
            <p className="text-2xl font-mono mt-2">{formatDuration(callDuration)}</p>

            {/* Attended transfer consultation controls */}
            {inConsultation && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={handleCompleteTransfer}
                  className="flex-1 flex items-center justify-center gap-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                >
                  <Check className="w-4 h-4" /> Complete
                </button>
                <button
                  onClick={handleCancelTransfer}
                  className="flex-1 flex items-center justify-center gap-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                >
                  <XCircle className="w-4 h-4" /> Cancel
                </button>
              </div>
            )}
          </div>

          {/* DTMF keypad during call */}
          <div className="p-3 border-b border-gray-100">
            <div className="grid grid-cols-3 gap-1">
              {['1','2','3','4','5','6','7','8','9','*','0','#'].map(d => (
                <button key={d} onClick={() => sendDTMF(d)}
                  className="py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-800 font-medium text-sm transition-colors">
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Call control buttons */}
          <div className="grid grid-cols-4 gap-2 p-3">
            <button
              onClick={handleMute}
              className={`flex flex-col items-center justify-center py-2 rounded-lg text-xs font-medium transition-colors ${isMuted ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {isMuted ? <MicOff className="w-4 h-4 mb-0.5" /> : <Mic className="w-4 h-4 mb-0.5" />}
              {isMuted ? 'Unmute' : 'Mute'}
            </button>

            <button
              onClick={handleHold}
              disabled={inConsultation}
              className={`flex flex-col items-center justify-center py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${isOnHold ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {isOnHold ? <Play className="w-4 h-4 mb-0.5" /> : <Pause className="w-4 h-4 mb-0.5" />}
              {isOnHold ? 'Resume' : 'Hold'}
            </button>

            <button
              onClick={openTransferDialog}
              disabled={inConsultation}
              className="flex flex-col items-center justify-center py-2 rounded-lg text-xs font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              <PhoneForwarded className="w-4 h-4 mb-0.5" />
              Transfer
            </button>

            <button
              onClick={handleHangup}
              className="flex flex-col items-center justify-center py-2 rounded-lg text-xs font-medium transition-colors bg-red-500 hover:bg-red-600 text-white"
            >
              <PhoneOff className="w-4 h-4 mb-0.5" />
              End
            </button>
          </div>
        </div>
      </>
    );
  }

  // Save contact prompt
  if (view === 'save_contact') {
    return (
      <div className="fixed bottom-6 right-6 z-[9999] w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Save as contact?</h3>
          <button onClick={() => setView('minimised')} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-500">Unknown caller: <span className="font-mono font-medium text-gray-800">{incomingNumber}</span></p>
          <input value={saveForm.first_name} onChange={e => setSaveForm(f => ({ ...f, first_name: e.target.value }))}
            placeholder="First name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          <input value={saveForm.last_name} onChange={e => setSaveForm(f => ({ ...f, last_name: e.target.value }))}
            placeholder="Last name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          <input value={saveForm.company_name} onChange={e => setSaveForm(f => ({ ...f, company_name: e.target.value }))}
            placeholder="Company (optional)" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          {status === 'in_call' && (
            <div className="flex gap-2 pt-2 pb-1 border-t border-gray-100 mt-1">
              <button onClick={() => setView('in_call')} className="flex-1 flex items-center justify-center gap-1 py-2 border border-blue-300 text-blue-600 rounded-lg text-sm hover:bg-blue-50">
                <Phone className="w-3.5 h-3.5" /> Back to call
              </button>
              <button onClick={() => { hangup(); setView('minimised'); }} className="flex-1 flex items-center justify-center gap-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600">
                <PhoneOff className="w-3.5 h-3.5" /> Hang up
              </button>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={() => setView(status === 'in_call' ? 'in_call' : 'minimised')} className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">Skip</button>
            <button onClick={handleSaveContact} disabled={saving || !saveForm.first_name}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Minimised button
  if (view === 'minimised') {
    return (
      <>
        {/* Disconnected call warning banner */}
        {wasDisconnected && (
          <div className="fixed bottom-24 right-6 z-[9999] w-72 bg-amber-50 border border-amber-200 rounded-xl shadow-lg p-3">
            <div className="flex items-start gap-2">
              <PhoneOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Call Disconnected</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Your {wasDisconnected.direction} call to {wasDisconnected.number} was disconnected when the page refreshed.
                </p>
              </div>
              <button
                onClick={() => setWasDisconnected(null)}
                className="text-amber-400 hover:text-amber-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => setView(status === 'in_call' ? 'in_call' : 'dialpad')}
          title={status === 'in_call' ? 'Return to call' : 'Open phone'}
          className={`fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 border-2 ${status === 'in_call' ? 'bg-blue-500 border-blue-400 animate-pulse' : 'bg-white border-gray-200'}`}
        >
          <StatusDot status={status} />
          <Phone className={`w-6 h-6 ml-1 ${status === 'registered' ? 'text-green-600' : status === 'in_call' ? 'text-white' : 'text-gray-500'}`} />
        </button>
      </>
    );
  }

  // Expanded panel (dialpad / calls / settings)
  const tabs: { id: WidgetView; icon: React.ReactNode; label: string }[] = [
    { id: 'dialpad', icon: <Phone className="w-4 h-4" />, label: 'Dial' },
    { id: 'calls', icon: <Clock className="w-4 h-4" />, label: 'Calls' },
    { id: 'settings', icon: <Settings className="w-4 h-4" />, label: 'Settings' },
  ];

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <StatusDot status={status} />
          <span className="text-sm font-medium text-gray-700 capitalize">
            {status === 'registered' ? `Ext ${settings.extension}` : status === 'failed' ? 'Connection failed' : status === 'disconnected' ? 'Reconnecting...' : status}
          </span>
        </div>
        <button onClick={() => setView('minimised')} className="text-gray-400 hover:text-gray-600">
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-xs font-medium transition-colors ${view === t.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* Dial pad */}
      {view === 'dialpad' && (
        <div className="p-4 space-y-3">
          <input value={dialInput} onChange={e => setDialInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleDial()}
            placeholder="Enter number..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          <div className="grid grid-cols-3 gap-2">
            {['1','2','3','4','5','6','7','8','9','*','0','#'].map(d => (
              <button key={d} onClick={() => handleDialpadKey(d)}
                className="py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium text-sm transition-colors">
                {d}
              </button>
            ))}
          </div>
          <button onClick={handleDial} disabled={!dialInput || status === 'in_call' || status !== 'registered'}
            className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors">
            <Phone className="w-4 h-4" /> Call
          </button>
        </div>
      )}

      {/* Recent calls */}
      {view === 'calls' && (
        <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
          {recentCalls.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">No calls yet this session</div>
          ) : recentCalls.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => { setDialInput(c.number); setView('dialpad'); }}>
              <div>
                <p className="text-sm font-medium text-gray-800">{c.number}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded ${c.direction === 'inbound' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {c.direction === 'inbound' ? '↙ In' : '↗ Out'}
                </span>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">{formatDuration(c.duration)}</p>
                <p className="text-xs text-gray-300">{new Date(c.ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Settings */}
      {view === 'settings' && (
        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          <p className="text-xs text-gray-500 mb-2">Enter your Asterisk extension credentials.</p>

          {/* Basic Settings */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Extension</label>
            <input value={settingsDraft.extension} onChange={e => setSettingsDraft(s => ({ ...s, extension: e.target.value }))} placeholder="e.g. 103"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SIP Password</label>
            <input type="password" value={settingsDraft.password} onChange={e => setSettingsDraft(s => ({ ...s, password: e.target.value }))} placeholder="SIP secret"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name (optional)</label>
            <input value={settingsDraft.displayName} onChange={e => setSettingsDraft(s => ({ ...s, displayName: e.target.value }))} placeholder="Your Name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>

          {/* VPN Mode Toggle */}
          <label className="flex items-center gap-2 cursor-pointer py-2">
            <input type="checkbox" checked={settingsDraft.vpnMode} onChange={e => setSettingsDraft(s => ({ ...s, vpnMode: e.target.checked }))}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
            <div>
              <span className="text-sm font-medium text-gray-700">VPN Mode</span>
              <p className="text-xs text-gray-400">Disable STUN/TURN for faster internal connection</p>
            </div>
          </label>

          {/* Advanced Settings Toggle */}
          <button onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className="w-full text-left text-xs text-blue-600 hover:text-blue-700 py-1">
            {showAdvancedSettings ? '▼ Hide advanced settings' : '▶ Show advanced settings'}
          </button>

          {showAdvancedSettings && (
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">WebSocket URL</label>
                <input value={settingsDraft.wsServer} onChange={e => setSettingsDraft(s => ({ ...s, wsServer: e.target.value }))}
                  placeholder="wss://support.mrvhub.com/ast-ws"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">SIP Domain</label>
                <input value={settingsDraft.sipDomain} onChange={e => setSettingsDraft(s => ({ ...s, sipDomain: e.target.value }))}
                  placeholder="192.168.50.14"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>

              {!settingsDraft.vpnMode && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">STUN Server</label>
                      <input value={settingsDraft.stunServer} onChange={e => setSettingsDraft(s => ({ ...s, stunServer: e.target.value }))}
                        placeholder="192.168.50.14:3478"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">TURN Server</label>
                      <input value={settingsDraft.turnServer} onChange={e => setSettingsDraft(s => ({ ...s, turnServer: e.target.value }))}
                        placeholder="192.168.50.14:3478"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">TURN Username</label>
                      <input value={settingsDraft.turnUser} onChange={e => setSettingsDraft(s => ({ ...s, turnUser: e.target.value }))}
                        placeholder="username"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">TURN Password</label>
                      <input type="password" value={settingsDraft.turnPass} onChange={e => setSettingsDraft(s => ({ ...s, turnPass: e.target.value }))}
                        placeholder="password"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Test Audio Button */}
          <button onClick={testAudio} className="w-full py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2">
            <Volume2 className="w-4 h-4" /> Test Audio Output
          </button>

          {/* Custom Ringtone */}
          <div className="border-t border-gray-100 pt-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Custom Ringtone (MP3 / OGG / WAV)</label>
            <div className="flex gap-2 items-center">
              <label className="flex-1 flex items-center justify-center gap-1 py-2 border border-gray-300 rounded-lg text-xs text-gray-600 cursor-pointer hover:bg-gray-50 truncate">
                <Volume2 className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{settingsDraft.ringtone ? '✓ Custom file set' : 'Upload file...'}</span>
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 1024 * 1024) { alert('File too large. Please use a ringtone under 1 MB.'); return; }
                    const reader = new FileReader();
                    reader.onload = (ev) => setSettingsDraft(s => ({ ...s, ringtone: ev.target?.result as string }));
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              {settingsDraft.ringtone && (
                <>
                  <button
                    onClick={() => { const a = new Audio(settingsDraft.ringtone); a.play().catch(() => {}); }}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-xs text-blue-600 hover:bg-blue-50"
                    title="Preview ringtone"
                  >▶</button>
                  <button
                    onClick={() => setSettingsDraft(s => ({ ...s, ringtone: '' }))}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-xs text-red-500 hover:bg-red-50"
                    title="Remove custom ringtone"
                  >✕</button>
                </>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">Max 1 MB. Stored locally in your browser.</p>
          </div>

          <button onClick={handleSaveSettings} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors">
            Save &amp; Connect
          </button>
        </div>
      )}
    </div>
  );
}

export default SoftphoneWidget;
